import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Spinner,
  Table,
  Form,
  Modal,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaTruck,
  FaUndo,
  FaExchangeAlt,
  FaCheck,
  FaTimes,
  FaInbox,
  FaFileInvoice,
  FaImages,
  FaVideo,
  FaExternalLinkAlt,
  FaBox,
  FaShippingFast,
  FaCheckCircle,
  FaPrint,
  FaCopy,
} from "react-icons/fa";
import { formatDateTimeToDDMMYYYY12Hr } from "../../utils/dateUtils";

const ReturnDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const isVendor = user?.user_type === "vendor";
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [courierPartner, setCourierPartner] = useState("xpressbees");
  const [adminNotes, setAdminNotes] = useState("");
  const [previewImage, setPreviewImage] = useState(null);

  // Exchange Replacement Dispatch State
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [replacementCourier, setReplacementCourier] = useState("xpressbees");
  const [replacementAwb, setReplacementAwb] = useState("");
  const [dispatchingReplacement, setDispatchingReplacement] = useState(false);

  const vendorItemsTotal = (request?.items || []).reduce(
    (sum, it) => sum + (parseFloat(it.subtotal) || (parseFloat(it.price || 0) * parseInt(it.quantity || 0))),
    0
  );

  const getImagesList = () => {
    if (!request) return [];
    let list = [];
    if (Array.isArray(request.images)) {
      list = [...request.images];
    } else if (typeof request.images === "string") {
      try {
        const parsed = JSON.parse(request.images);
        if (Array.isArray(parsed)) list = [...parsed];
        else list.push(request.images);
      } catch (e) {
        if (request.images.trim()) list.push(request.images.trim());
      }
    }
    if (request.image_url && !list.includes(request.image_url)) {
      list.push(request.image_url);
    }
    return Array.from(new Set(list.filter(Boolean)));
  };

  const getVideosList = () => {
    if (!request) return [];
    let list = [];
    if (Array.isArray(request.videos)) {
      list = [...request.videos];
    } else if (typeof request.videos === "string") {
      try {
        const parsed = JSON.parse(request.videos);
        if (Array.isArray(parsed)) list = [...parsed];
        else list.push(request.videos);
      } catch (e) {
        if (request.videos.trim()) list.push(request.videos.trim());
      }
    }
    if (request.video_url && !list.includes(request.video_url)) {
      list.push(request.video_url);
    }
    return Array.from(new Set(list.filter(Boolean)));
  };

  useEffect(() => {
    fetchReturnDetails();
  }, [id]);

  const fetchReturnDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/returns/${id}`);
      if (response.data && response.data.success) {
        setRequest(response.data.data);
        setAdminNotes(response.data.data.notes || "");
      } else {
        toast.error("Failed to load request details");
      }
    } catch (err) {
      console.error("Error fetching return details:", err);
      toast.error("Failed to load return details");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!courierPartner) {
      toast.warning("Please select a courier partner for reverse logistics.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.patch(`/api/returns/${id}/status`, {
        status: "approved",
        courier_partner: courierPartner,
        notes: adminNotes,
      });

      if (response.data && response.data.success) {
        toast.success("Return approved and reverse pickup scheduled successfully!");
        fetchReturnDetails();
      } else {
        toast.error("Failed to approve return request");
      }
    } catch (err) {
      console.error("Error approving return:", err);
      toast.error(err.response?.data?.message || "Failed to schedule reverse logistics");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setSubmitting(true);
    try {
      const response = await api.patch(`/api/returns/${id}/status`, {
        status: newStatus,
        notes: adminNotes,
      });

      if (response.data && response.data.success) {
        toast.success(`Request status updated to ${newStatus.replace("_", " ")}!`);
        fetchReturnDetails();
      } else {
        toast.error("Failed to update status");
      }
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error(err.response?.data?.message || "Failed to update return status");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDispatchReplacement = async () => {
    setDispatchingReplacement(true);
    try {
      const response = await api.post(`/api/returns/${id}/dispatch-replacement`, {
        courier_partner: replacementCourier,
        replacement_awb_number: replacementAwb.trim() || undefined,
        notes: adminNotes || undefined,
      });

      if (response.data && response.data.success) {
        toast.success("Exchange replacement shipment dispatched successfully!");
        setShowDispatchModal(false);
        fetchReturnDetails();
      } else {
        toast.error("Failed to dispatch replacement");
      }
    } catch (err) {
      console.error("Error dispatching replacement:", err);
      toast.error(err.response?.data?.message || "Failed to dispatch replacement");
    } finally {
      setDispatchingReplacement(false);
    }
  };

  const getStatusBadge = (status) => {
    const key = status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    const statusMap = {
      pending: { variant: "warning", text: "Pending Review" },
      approved: { variant: "info", text: "Approved" },
      pickup_scheduled: { variant: "primary", text: "Pickup Scheduled" },
      pickup_completed: { variant: "info", text: "In Transit" },
      received: { variant: "secondary", text: "Package Received" },
      replacement_dispatched: { variant: "primary", text: "Replacement Dispatched" },
      completed: { variant: "success", text: "Completed" },
      rejected: { variant: "danger", text: "Rejected" },
      cancelled: { variant: "dark", text: "Cancelled" },
    };

    const config = statusMap[key] || {
      variant: "secondary",
      text: status ? status.replace("_", " ").toUpperCase() : "UNKNOWN",
    };

    return (
      <Badge bg={config.variant} className="px-3 py-2 fs-6">
        {config.text}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!request) {
    return (
      <Container className="view-form">
        <div className="text-center mt-5">
          <h4>Return request not found</h4>
          <Button
            variant="primary"
            className="mt-3"
            onClick={() => navigate("/admin/returns")}
          >
            <FaArrowLeft className="me-2" /> Back to Returns & Exchanges
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="view-form pb-4">
      {/* Breadcrumb Header */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <Link className="breadcrum" to="/">
              Home{"/"}
              <i
                className="fa-solid fa-chevron-right"
                style={{ marginLeft: "1px", marginRight: "1px" }}
              ></i>{" "}
              <Link to="/admin/returns" className="text-decoration-none">
                Returns & Exchanges
              </Link>{" "}
              {"/"} Details
            </Link>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate("/admin/returns")}
            >
              <FaArrowLeft className="me-1" /> Back
            </Button>
          </div>
        </Col>
      </Row>

      {/* Main Details Card */}
      <Row>
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0 fw-semibold">
                Return Request #{request.id.slice(0, 8).toUpperCase()}
              </h5>
              {isVendor && (
                <Badge bg="info" className="px-2 py-1 text-dark" style={{ fontSize: "11px", fontWeight: "600" }}>
                  Vendor Portal View
                </Badge>
              )}
            </div>
            <div>{getStatusBadge(request.status)}</div>
          </div>

          <Card className="shadow-sm mb-4">
            <CardBody className="p-4">
              <Row className="g-4">
                {/* Left Column: Request Details & Items */}
                <Col lg={8}>
                  <div className="p-3 bg-light rounded-3 mb-4">
                    <Row>
                      <Col md={6} className="mb-3 mb-md-0">
                        <label className="text-muted small fw-bold text-uppercase d-block mb-1">
                          Request Type
                        </label>
                        {request.type === "exchange" ? (
                          <span className="text-primary fw-bold fs-5 d-inline-flex align-items-center">
                            <FaExchangeAlt className="me-2" /> Product Exchange
                          </span>
                        ) : (
                          <span className="text-success fw-bold fs-5 d-inline-flex align-items-center">
                            <FaUndo className="me-2" /> Return & Refund
                          </span>
                        )}
                      </Col>
                      <Col md={6}>
                        <label className="text-muted small fw-bold text-uppercase d-block mb-1">
                          Request Date
                        </label>
                        <span className="fw-semibold">
                          {formatDateTimeToDDMMYYYY12Hr(request.created_at)}
                        </span>
                      </Col>
                    </Row>
                    <hr className="my-3" />
                    <div>
                      <label className="text-muted small fw-bold text-uppercase d-block mb-1">
                        Reason for Return / Exchange
                      </label>
                      <p className="fw-semibold mb-1">{request.reason}</p>
                      {request.notes && (
                        <small className="text-muted">
                          <strong>Customer Note:</strong> {request.notes}
                        </small>
                      )}
                    </div>

                    {request.upi_id && (
                      <div className="mt-3 p-3 bg-light border border-primary-subtle rounded-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                          <label className="text-muted small fw-bold text-uppercase d-block mb-1">
                            Online Refund UPI ID
                          </label>
                          <span className="fw-bold font-monospace text-primary fs-6">
                            {request.upi_id}
                          </span>
                        </div>
                        <Button
                          // variant="outline-primary"
                          size="sm"
                          className="d-inline-flex align-items-center"
                          onClick={() => {
                            navigator.clipboard.writeText(request.upi_id);
                            toast.success("UPI ID copied to clipboard!");
                          }}
                        >
                          <FaCopy className="me-1" /> Copy UPI
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Customer Uploaded Proof Images & Videos */}
                  {(getImagesList().length > 0 || getVideosList().length > 0) && (
                    <div className="p-3 border rounded-3 bg-white mb-4 shadow-sm">
                      <h6 className="fw-bold mb-3 d-flex align-items-center text-dark border-bottom pb-2">
                        <FaImages className="me-2 text-primary" /> Customer Proof Images & Videos
                      </h6>

                      {/* Image Proofs */}
                      {getImagesList().length > 0 && (
                        <div className="mb-3">
                          <small className="text-muted fw-bold d-block mb-2 text-uppercase">
                            Attached Photos ({getImagesList().length})
                          </small>
                          <div className="d-flex flex-wrap gap-3">
                            {getImagesList().map((imgUrl, idx) => (
                              <div
                                key={idx}
                                className="position-relative border rounded p-1 bg-light shadow-sm"
                                style={{ width: "130px", height: "130px" }}
                              >
                                <img
                                  src={imgUrl}
                                  alt={`Proof ${idx + 1}`}
                                  className="w-100 h-100 rounded"
                                  style={{ objectFit: "cover", cursor: "pointer" }}
                                  onClick={() => setPreviewImage(imgUrl)}
                                  title="Click to view full size"
                                />
                                <a
                                  href={imgUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-dark position-absolute bottom-0 end-0 m-1 py-0 px-1 opacity-75"
                                  title="Open original link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <FaExternalLinkAlt size={10} />
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Video Proofs */}
                      {getVideosList().length > 0 && (
                        <div>
                          <small className="text-muted fw-bold d-block mb-2 text-uppercase d-flex align-items-center">
                            <FaVideo className="me-1 text-danger" /> Attached Videos ({getVideosList().length})
                          </small>
                          <div className="d-flex flex-wrap gap-3">
                            {getVideosList().map((vidUrl, idx) => (
                              <div
                                key={idx}
                                className="border rounded p-1 bg-dark shadow-sm"
                                style={{ width: "240px" }}
                              >
                                <video
                                  controls
                                  className="w-100 rounded"
                                  style={{ maxHeight: "150px" }}
                                >
                                  <source src={vidUrl} />
                                  Your browser does not support the video tag.
                                </video>
                                <div className="text-end pt-1 px-1">
                                  <a
                                    href={vidUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-white-50 small text-decoration-none"
                                  >
                                    <FaExternalLinkAlt className="me-1" size={10} /> Open in new tab
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Items Table */}
                  <h6 className="fw-bold mb-3">Items for Return / Exchange</h6>
                  <div className="table-responsive mb-4 shadow-sm">
                    <Table bordered hover className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th className="text-center" style={{ width: "100px" }}>
                            Qty
                          </th>
                          <th className="text-end" style={{ width: "130px" }}>
                            Price
                          </th>
                          <th className="text-end" style={{ width: "140px" }}>
                            Subtotal
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {request.items &&
                          request.items.map((item, idx) => (
                            <tr key={idx}>
                              <td className="fw-semibold">
                                {item.product_name || "Book Item"}
                              </td>
                              <td className="text-center">{item.quantity}</td>
                              <td className="text-end">
                                ₹{parseFloat(item.price || 0).toFixed(2)}
                              </td>
                              <td className="text-end fw-bold">
                                ₹{parseFloat(item.subtotal || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                      {request.type === "return" && (
                        <tfoot>
                          <tr className="table-light">
                            <th colSpan="3" className="text-end">
                              {isVendor ? "Estimated Refund Amount (Your Items):" : "Estimated Refund Amount:"}
                            </th>
                            <th className="text-end text-success fw-bold fs-6">
                              ₹{parseFloat((isVendor && request.vendor_refund_amount !== undefined) ? request.vendor_refund_amount : (request.refund_amount || 0)).toFixed(2)}
                            </th>
                          </tr>
                        </tfoot>
                      )}
                    </Table>
                  </div>

                  {/* Exchange 3-Shipment Delivery Tracker */}
                  {request.type === "exchange" ? (
                    <div className="p-3 border rounded-3 bg-white mb-4 shadow-sm">
                      <h6 className="fw-bold text-dark mb-3 d-flex align-items-center border-bottom pb-2">
                        <FaExchangeAlt className="me-2 text-primary" /> Exchange 3-Shipment Delivery Tracker
                      </h6>

                      <div className="row g-3">
                        {/* Shipment 1: Original Order */}
                        <div className="col-md-4">
                          <div className="border rounded-3 p-3 bg-light h-100 position-relative">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="badge bg-secondary">Shipment 1</span>
                              <span className="badge bg-success"><FaCheckCircle className="me-1" />Delivered</span>
                            </div>
                            <h6 className="fw-bold mb-1 d-flex align-items-center text-dark">
                              <FaBox className="me-2 text-secondary" /> Original Order
                            </h6>
                            <small className="text-muted d-block mb-1">
                              Delivered to Customer
                            </small>
                            <div className="mt-2 pt-2 border-top">
                              <small className="text-muted d-block">ORDER NO.</small>
                              <span className="fw-bold text-primary font-monospace">{request.order_no || `#ORD-${request.order_id?.slice(-8)}`}</span>
                            </div>
                          </div>
                        </div>

                        {/* Shipment 2: Reverse Pickup */}
                        <div className="col-md-4">
                          <div className={`border rounded-3 p-3 h-100 position-relative ${request.reverse_awb_number ? 'bg-light' : 'bg-white'}`}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="badge bg-secondary">Shipment 2</span>
                              {['received', 'replacement_dispatched', 'completed'].includes(request.status) ? (
                                <span className="badge bg-success"><FaCheckCircle className="me-1" />Item Received</span>
                              ) : request.reverse_awb_number ? (
                                <span className="badge bg-primary">Pickup In Transit</span>
                              ) : (
                                <span className="badge bg-warning text-dark">Pending Booking</span>
                              )}
                            </div>
                            <h6 className="fw-bold mb-1 d-flex align-items-center text-dark">
                              <FaTruck className="me-2 text-warning" /> Reverse Pickup
                            </h6>
                            <small className="text-muted d-block mb-1">
                              Customer &rarr; Warehouse
                            </small>
                            <div className="mt-2 pt-2 border-top">
                              <small className="text-muted d-block">REVERSE AWB</small>
                              <span className="font-monospace fw-bold text-dark">
                                {request.reverse_awb_number || "Not generated yet"}
                              </span>
                              {request.courier_partner && (
                                <small className="text-muted d-block text-uppercase mt-1">
                                  via {request.courier_partner}
                                </small>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Shipment 3: Replacement Delivery */}
                        <div className="col-md-4">
                          <div className={`border rounded-3 p-3 h-100 position-relative ${request.replacement_awb_number ? 'bg-light' : 'bg-white'}`}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="badge bg-secondary">Shipment 3</span>
                              {request.replacement_status === "delivered" || request.status === "completed" ? (
                                <span className="badge bg-success"><FaCheckCircle className="me-1" />Delivered</span>
                              ) : request.replacement_awb_number ? (
                                <span className="badge bg-primary">Dispatched</span>
                              ) : (
                                <span className="badge bg-secondary">Pending Dispatch</span>
                              )}
                            </div>
                            <h6 className="fw-bold mb-1 d-flex align-items-center text-dark">
                              <FaShippingFast className="me-2 text-primary" /> Replacement Delivery
                            </h6>
                            <small className="text-muted d-block mb-1">
                              Warehouse &rarr; Customer
                            </small>
                            <div className="mt-2 pt-2 border-top">
                              <small className="text-muted d-block">REPLACEMENT AWB</small>
                              <span className="font-monospace fw-bold text-primary">
                                {request.replacement_awb_number || "Awaiting dispatch"}
                              </span>
                              {request.replacement_courier_partner && (
                                <small className="text-muted d-block text-uppercase mt-1">
                                  via {request.replacement_courier_partner}
                                </small>
                              )}
                              {request.replacement_shipping_label_url && (
                                <a
                                  href={request.replacement_shipping_label_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-xs btn-outline-primary mt-2 py-0 px-2 small d-inline-flex align-items-center"
                                >
                                  <FaPrint className="me-1" /> Print Label
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Standard Reverse Courier Tracking Details for Return */
                    request.reverse_awb_number && (
                      <div className="p-3 border rounded-3 bg-light mb-4">
                        <h6 className="fw-bold text-primary mb-3 d-flex align-items-center">
                          <FaTruck className="me-2" /> Reverse Logistics Tracking
                        </h6>
                        <Row>
                          <Col sm={6} className="mb-2 mb-sm-0">
                            <small className="text-muted d-block">COURIER PARTNER</small>
                            <span className="fw-bold text-uppercase">
                              {request.courier_partner}
                            </span>
                          </Col>
                          <Col sm={6}>
                            <small className="text-muted d-block">AWB / TRACKING NUMBER</small>
                            <span className="font-monospace fw-bold text-dark">
                              {request.reverse_awb_number}
                            </span>
                          </Col>
                        </Row>
                      </div>
                    )
                  )}

                  {/* Processing Notes Section */}
                  <div>
                    <label className="form-label fw-bold">
                      Processing Notes
                    </label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Add internal remarks or customer communication updates..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                    />
                  </div>
                </Col>

                {/* Right Column: Customer Details & Actions */}
                <Col lg={4}>
                  {/* Customer Information Card */}
                  <div className="border rounded-3 p-3 bg-light mb-4">
                    <h6 className="fw-bold mb-3 border-bottom pb-2">
                      Customer & Order Summary
                    </h6>
                    <div className="mb-3">
                      <small className="text-muted d-block">CUSTOMER NAME</small>
                      <span className="fw-bold">
                        {request.customer_first_name} {request.customer_last_name}
                      </span>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted d-block">PHONE NUMBER</small>
                      <span className="fw-bold">{request.customer_phone || "-"}</span>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted d-block">EMAIL</small>
                      <span className="fw-semibold">{request.customer_email || "-"}</span>
                    </div>
                    {request.upi_id && (
                      <div className="mb-3">
                        <small className="text-muted d-block">REFUND UPI ID</small>
                        <span className="fw-bold font-monospace text-primary">{request.upi_id}</span>
                      </div>
                    )}
                    <hr />
                    <div className="mb-3">
                      <small className="text-muted d-block">ORDER NUMBER</small>
                      <Link
                        to={`/admin/orders/details/${request.order_id}`}
                        className="fw-bold text-decoration-none text-primary"
                      >
                        {request.order_no || `ORD-${request.order_id.slice(-8).toUpperCase()}`}
                      </Link>
                    </div>
                    <div>
                      <small className="text-muted d-block">
                        {isVendor ? "YOUR STORE ITEMS TOTAL" : "ORDER TOTAL"}
                      </small>
                      <span className="fw-bold text-success">
                        ₹{parseFloat(isVendor ? vendorItemsTotal : (request.order_final_amount || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Action / Workflow Card */}
                  <div className="border rounded-3 p-3 bg-light">
                    <h6 className="fw-bold mb-3 border-bottom pb-2">
                      Workflow & Actions
                    </h6>

                    {request.status === "pending" && (
                      <div>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold small">
                            Reverse Courier Partner
                          </Form.Label>
                          <Form.Select
                            value={courierPartner}
                            onChange={(e) => setCourierPartner(e.target.value)}
                          >
                            <option value="xpressbees">XpressBees Courier</option>
                            <option value="shadowfax">Shadowfax Courier</option>
                            <option value="delhivery">Delhivery Courier</option>
                          </Form.Select>
                        </Form.Group>

                        <Button
                          onClick={handleApprove}
                          disabled={submitting}
                          variant="primary"
                          className="w-100 mb-2 py-2 fw-bold d-flex align-items-center justify-content-center"
                        >
                          {submitting ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                          ) : (
                            <FaCheck className="me-2" />
                          )}
                          Approve & Book Pickup
                        </Button>

                        <Button
                          onClick={() => handleStatusChange("rejected")}
                          disabled={submitting}
                          variant="outline-danger"
                          className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center"
                        >
                          <FaTimes className="me-2" /> Reject Request
                        </Button>
                      </div>
                    )}

                    {request.status === "pickup_scheduled" && (
                      <div>
                        <div className="alert alert-info text-center py-2 mb-3 small" role="alert">
                          Reverse pickup booked. Awaiting courier collection.
                        </div>
                        <Button
                          onClick={() => handleStatusChange("received")}
                          disabled={submitting}
                          variant="primary"
                          className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center"
                        >
                          {submitting ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                          ) : (
                            <FaInbox className="me-2" />
                          )}
                          Mark Package Received
                        </Button>
                      </div>
                    )}

                    {request.status === "received" && (
                      <div>
                        {request.type === "exchange" ? (
                          <>
                            <div className="alert alert-info text-center py-2 mb-3 small" role="alert">
                              Defective product received at warehouse. Ready to dispatch replacement.
                            </div>
                            <Button
                              onClick={() => setShowDispatchModal(true)}
                              variant="primary"
                              className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center"
                            >
                              <FaShippingFast className="me-2" /> Dispatch Replacement Item
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="alert alert-success text-center py-2 mb-3 small" role="alert">
                              {request.upi_id ? (
                                <>
                                  Package received. Customer requested online refund via UPI:{" "}
                                  <strong className="font-monospace">{request.upi_id}</strong>
                                </>
                              ) : (
                                "Package received at warehouse. Complete to restore stock and refund."
                              )}
                            </div>
                            <Button
                              onClick={() => handleStatusChange("completed")}
                              disabled={submitting}
                              variant="success"
                              className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center"
                            >
                              {submitting ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                              ) : (
                                <FaCheck className="me-2" />
                              )}
                              Complete Return & Process Refund
                            </Button>
                          </>
                        )}
                      </div>
                    )}

                    {request.status === "replacement_dispatched" && (
                      <div>
                        <div className="alert alert-primary text-center py-2 mb-3 small" role="alert">
                          Replacement item dispatched via <strong>{request.replacement_courier_partner || "Courier"}</strong>.
                          <div className="fw-bold mt-1 font-monospace">AWB: {request.replacement_awb_number}</div>
                        </div>
                        <Button
                          onClick={() => handleStatusChange("completed")}
                          disabled={submitting}
                          variant="success"
                          className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center"
                        >
                          {submitting ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                          ) : (
                            <FaCheckCircle className="me-2" />
                          )}
                          Mark Replacement Delivered
                        </Button>
                      </div>
                    )}

                    {request.status === "completed" && (
                      <div className="text-center py-3">
                        <div className="text-success fw-bold mb-2">
                          <FaCheckCircle className="fs-3 me-2" />
                          {request.type === "exchange" ? "Exchange Completed" : "Return Completed"}
                        </div>
                        <p className="text-muted small mb-0">
                          {request.type === "exchange"
                            ? "Replacement product successfully delivered to customer. Exchange closed."
                            : "Return completed. Items restored to inventory and refund recorded."}
                        </p>
                      </div>
                    )}

                    {request.status === "rejected" && (
                      <div className="text-center py-3">
                        <div className="text-danger fw-bold mb-2">
                          <FaTimes className="fs-3 me-2" />
                          Request Rejected
                        </div>
                        <p className="text-muted small mb-0">
                          This return request was reviewed and rejected.
                        </p>
                      </div>
                    )}

                    {request.status === "cancelled" && (
                      <div className="text-center py-3">
                        <div className="text-dark fw-bold mb-2">Cancelled</div>
                        <p className="text-muted small mb-0">
                          This return request was cancelled by customer or admin.
                        </p>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Image Preview Modal */}
      <Modal
        show={!!previewImage}
        onHide={() => setPreviewImage(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">Customer Proof Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-2 bg-dark">
          {previewImage && (
            <img
              src={previewImage}
              alt="Full size proof"
              className="img-fluid rounded"
              style={{ maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </Modal.Body>
        <Modal.Footer className="py-1">
          {previewImage && (
            <a
              href={previewImage}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-primary"
            >
              <FaExternalLinkAlt className="me-1" /> Open Original in New Tab
            </a>
          )}
          <Button variant="secondary" size="sm" onClick={() => setPreviewImage(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Dispatch Replacement Shipment Modal */}
      <Modal show={showDispatchModal} onHide={() => setShowDispatchModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">Dispatch Replacement Shipment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Dispatch the replacement product to the customer. This will generate a forward shipment tracking number (AWB) and update the customer's tracking lifecycle.
          </p>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold small">Courier Partner</Form.Label>
            <Form.Select
              value={replacementCourier}
              onChange={(e) => setReplacementCourier(e.target.value)}
            >
              <option value="xpressbees">XpressBees Surface</option>
              <option value="shadowfax">Shadowfax Surface</option>
              <option value="delhivery">Delhivery Surface</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold small">Tracking / AWB Number (Optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Leave empty to auto-generate from courier"
              value={replacementAwb}
              onChange={(e) => setReplacementAwb(e.target.value)}
            />
            <Form.Text className="text-muted">
              If left blank, the system will automatically generate or request an AWB from the courier.
            </Form.Text>
          </Form.Group>

          <div className="p-2 border rounded bg-light small">
            <div className="fw-bold text-dark mb-1">Customer Delivery Destination:</div>
            <div>{request.customer_first_name} {request.customer_last_name} ({request.customer_phone || "-"})</div>
            <div className="text-muted">{request.order_no || `#ORD-${request.order_id?.slice(-8)}`}</div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDispatchModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleDispatchReplacement}
            disabled={dispatchingReplacement}
          >
            {dispatchingReplacement ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Dispatching...
              </>
            ) : (
              "Confirm & Dispatch Shipment"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ReturnDetails;
